"use client";

import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import { useEffect, useRef } from "react";
import type ReactPlayerType from "react-player";
import type { ReactPlayerProps } from "react-player/lazy";
import { isYouTubeUrl } from "./utils";

// Dynamic import with SSR disabled to prevent hydration mismatch
const ReactPlayer = dynamic(() => import("react-player/lazy"), {
  ssr: false,
});

const videoPlayerVariants = {
  container:
    "absolute inset-0 w-full h-full rounded-[var(--border-radius-x6,12px)] overflow-hidden",
  player: "w-full h-full",
};

export interface MediaCarouselVideoPlayerProps {
  url: string;
  isActive: boolean;
  videoDomRef?: (el: HTMLVideoElement | null) => void;
  onVideoEnd?: () => void;
  onPlayingStateChange?: (isPlaying: boolean) => void;
  className?: string;
}

export const MediaCarouselVideoPlayer = ({
  url,
  isActive,
  videoDomRef,
  onVideoEnd,
  onPlayingStateChange,
  className,
}: MediaCarouselVideoPlayerProps) => {
  const playerRef = useRef<ReactPlayerType>(null);

  const isYouTube = isYouTubeUrl(url);

  const config = {
    youtube: {
      playerVars: { rel: 0 },
    },
  };

  const playerProps: ReactPlayerProps = {
    url: url,
    playing: isActive,
    controls: true,
    muted: true,
    loop: false,
    width: "100%",
    height: "100%",
    config: isYouTube ? config : undefined,
    onEnded: onVideoEnd,
    onPlay: () => onPlayingStateChange?.(true),
    onPause: () => onPlayingStateChange?.(false),
  };

  // Handle video element ref for all video types
  useEffect(() => {
    if (videoDomRef) {
      const videoElement =
        playerRef.current?.getInternalPlayer() as HTMLVideoElement;
      videoDomRef(videoElement);
    }
  }, [videoDomRef]);

  return (
    <div className={cn(videoPlayerVariants.container, className)}>
      <ReactPlayer {...playerProps} ref={playerRef} />
    </div>
  );
};
